<template>
  <v-row justify="center">
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="1024"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar :color="$t('Customer.CustomerSelectCompanyDialog.Toolbar.Color')">
            <v-icon dark>
              {{ $t('Customer.CustomerSelectCompanyDialog.Toolbar.Icon') }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t('Customer.CustomerSelectCompanyDialog.Toolbar.Title') }}
            </div>
            <div class="text-caption">
              {{ $t('Customer.CustomerSelectCompanyDialog.Toolbar.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-1">
          <v-form
            ref="companyForm"
            v-model="companyFormValid"
            @submit.prevent
          >
            <v-container fluid>
              <v-row dense>
                <v-col cols="12">
                  <v-stepper
                    v-model="step"
                    class="elevation-0"
                  >
                    <v-stepper-items>
                      <v-stepper-content step="1">
                        <v-container
                          class="mt-0 pt-0"
                          fluid
                        >
                          <v-row>
                            <v-col cols="12">
                              <v-autocomplete
                                v-model="selectedCompany"
                                :filter="filterCompanies"
                                :items="companiesList"
                                :label="$t('Customer.CustomerSelectCompanyDialog.Toolbar.SelectCompany')"
                                :prepend-icon="$t('Customer.CustomerSelectCompanyDialog.Toolbar.Icon')"
                                clearable
                                item-text="BrandName"
                                item-value="Id"
                                return-object
                              >
                                <template #item="dataItem">
                                  <v-list-item-content>
                                    <v-list-item-title>{{ dataItem.item.BrandName }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ dataItem.item.DistinctiveTitle | truncate }}</v-list-item-subtitle>
                                    <v-list-item-subtitle><strong>{{ $t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Label') }}:</strong> {{ dataItem.item.VatNumber }}</v-list-item-subtitle>
                                  </v-list-item-content>
                                </template>
                              </v-autocomplete>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-text-field
                                v-model="company.VatNumber"
                                :disabled="!!selectedCompany"
                                :error-messages="vatNumberError"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Label')"
                                :prefix="vatCountryCode"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Icon')"
                                :rules="validationRules.companyForm.VatNumber"
                                required
                              >
                                <template #append>
                                  <v-btn
                                    :disabled="!company.VatNumber || !!selectedCompany || fetchingVatData || !validateVAT(vatCountryCode, company.VatNumber)"
                                    :loading="fetchingVatData"
                                    color="primary"
                                    icon
                                    small
                                    @click="fetchDataByVatNumber"
                                  >
                                    <v-icon>mdi-database-arrow-down</v-icon>
                                  </v-btn>
                                </template>
                              </v-text-field>
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-autocomplete
                                v-model="company.CountryId"
                                :disabled="!!selectedCompany"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.Country.Hint')"
                                :items="countryList"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.Country.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Country.Icon')"
                                :rules="validationRules.companyForm.CountryId"
                                cache-items
                                class="pr-2"
                                item-text="name"
                                item-value="id"
                                required
                              >
                                <template #prepend>
                                  <v-avatar
                                    size="22"
                                    tile
                                  >
                                    <template v-if="vatCountryCode">
                                      <v-img :src="`/assets/flags/1x1/${String(vatCountryCode).toLowerCase()}.svg`" />
                                    </template>
                                    <template v-else>
                                      <v-icon>map</v-icon>
                                    </template>
                                  </v-avatar>
                                </template>

                                <template #item="{item}">
                                  <v-list-item-avatar size="34">
                                    <v-img :src="`/assets/flags/1x1/${String(item.code).toLowerCase()}.svg`" />
                                  </v-list-item-avatar>
                                  <v-list-item-content>
                                    <v-list-item-title v-safe-html="item.name" />
                                    <v-list-item-subtitle
                                      v-safe-html="item.desc"
                                      class="text-caption"
                                    />
                                  </v-list-item-content>
                                </template>
                              </v-autocomplete>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-stepper-content>

                      <v-stepper-content step="2">
                        <v-container
                          class="mt-0 pt-0"
                          fluid
                        >
                          <!-- Company Info -->
                          <v-row dense>
                            <v-col cols="12">
                              <v-subheader class="white--text primary text-subtitle-1 ma-0">
                                <v-icon
                                  class="pr-2"
                                  dark
                                >
                                  mdi-domain
                                </v-icon>
                                {{ $t('Customer.CustomerAddCompanyDialog.Company.Title') }}
                              </v-subheader>
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-text-field
                                v-model="company.BrandName"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.BrandName.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.BrandName.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.BrandName.Icon')"
                                :rules="validationRules.companyForm.BrandName"
                                required
                              />
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-text-field
                                v-model="company.DistinctiveTitle"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.DistinctiveTitle.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.DistinctiveTitle.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.DistinctiveTitle.Icon')"
                                :rules="validationRules.companyForm.DistinctiveTitle"
                                required
                              />
                            </v-col>

                            <v-col cols="12">
                              <v-text-field
                                v-model="company.Profession"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.Profession.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.Profession.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Profession.Icon')"
                                :rules="validationRules.companyForm.Profession"
                                required
                              />
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-text-field
                                v-model="company.Phone"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.Phone.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.Phone.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Phone.Icon')"
                                :rules="validationRules.companyForm.Phone"
                                required
                              />
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-text-field
                                v-model="company.Email"
                                :counter="96"
                                :hint="$t('Customer.CustomerAddEditDialog.Field.Info.Email.Hint')"
                                :label="$t('Customer.CustomerAddEditDialog.Field.Info.Email.Label')"
                                :prepend-icon="$t('Customer.CustomerAddEditDialog.Field.Info.Email.Icon')"
                                :rules="validationRules.companyForm.Email"
                              />
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-text-field
                                v-model="company.VatNumber"
                                :disabled="!!selectedCompany"
                                :error-messages="vatNumberError"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Label')"
                                :prefix="vatCountryCode"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Icon')"
                                :rules="validationRules.companyForm.VatNumber"
                                required
                              >
                                <template #append>
                                  <v-btn
                                    :disabled="!company.VatNumber || !!selectedCompany || fetchingVatData || !validateVAT(vatCountryCode, company.VatNumber)"
                                    :loading="fetchingVatData"
                                    color="primary"
                                    icon
                                    small
                                    @click="fetchDataByVatNumber"
                                  >
                                    <v-icon>mdi-database-arrow-down</v-icon>
                                  </v-btn>
                                </template>
                              </v-text-field>
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-text-field
                                v-model="company.Irs"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.Irs.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.Irs.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Irs.Icon')"
                                :rules="validationRules.companyForm.Irs"
                                required
                              />
                            </v-col>
                          </v-row>

                          <!-- Charge Address -->
                          <v-row dense>
                            <v-col cols="12">
                              <v-subheader class="white--text primary text-subtitle-1 ma-0">
                                <v-icon
                                  class="pr-2"
                                  dark
                                >
                                  my_location
                                </v-icon>
                                {{ $t('Customer.CustomerAddCompanyDialog.Address.Charge.Title') }}
                              </v-subheader>
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-text-field
                                v-model="company.Street"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.Street.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.Street.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Street.Icon')"
                                :rules="validationRules.companyForm.Street"
                                required
                              />
                            </v-col>

                            <v-col
                              cols="12"
                              sm="3"
                            >
                              <v-text-field
                                v-model="company.StreetNumber"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.StreetNumber.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.StreetNumber.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.StreetNumber.Icon')"
                                :rules="validationRules.companyForm.StreetNumber"
                                required
                              />
                            </v-col>

                            <v-col
                              cols="12"
                              sm="3"
                            >
                              <v-text-field
                                v-model="company.Postcode"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.Postcode.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.Postcode.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Postcode.Icon')"
                                :rules="validationRules.companyForm.Postcode"
                                required
                              />
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-text-field
                                v-model="company.City"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.City.Hint')"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.City.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.City.Icon')"
                                :rules="validationRules.companyForm.City"
                                required
                              />
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-autocomplete
                                v-model="company.CountryId"
                                :disabled="!!selectedCompany"
                                :hint="$t('Customer.CustomerAddCompanyDialog.Field.Country.Hint')"
                                :items="countryList"
                                :label="$t('Customer.CustomerAddCompanyDialog.Field.Country.Label')"
                                :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Country.Icon')"
                                :rules="validationRules.companyForm.CountryId"
                                cache-items
                                class="pr-2"
                                item-text="name"
                                item-value="id"
                                required
                              >
                                <template #prepend>
                                  <v-avatar
                                    size="22"
                                    tile
                                  >
                                    <template v-if="company.CountryId">
                                      <v-img :src="`/assets/flags/1x1/${String(countryList.find(c => c.id === company.CountryId)?.code).toLowerCase()}.svg`" />
                                    </template>
                                    <template v-else>
                                      <v-icon>map</v-icon>
                                    </template>
                                  </v-avatar>
                                </template>

                                <template #item="{item}">
                                  <v-list-item-avatar size="34">
                                    <v-img :src="`/assets/flags/1x1/${String(item.code).toLowerCase()}.svg`" />
                                  </v-list-item-avatar>
                                  <v-list-item-content>
                                    <v-list-item-title v-safe-html="item.name" />
                                    <v-list-item-subtitle
                                      v-safe-html="item.desc"
                                      class="text-caption"
                                    />
                                  </v-list-item-content>
                                </template>
                              </v-autocomplete>
                            </v-col>
                          </v-row>

                          <!-- Delivery Address -->
                          <v-row
                            v-if="!isTableOrder"
                            dense
                          >
                            <v-col cols="12">
                              <v-subheader class="white--text primary text-subtitle-1 ma-0">
                                <v-icon
                                  class="pr-2"
                                  dark
                                >
                                  my_location
                                </v-icon>
                                {{ $t('Customer.CustomerAddCompanyDialog.Address.Delivery.Title') }}
                                <v-spacer />

                                <v-switch
                                  v-model="company.DestinationDetails.Active"
                                  color="white"
                                  inset
                                  label=""
                                  light
                                >
                                  <template #prepend>
                                    <div
                                      class="white--text text-caption text-no-wrap"
                                      style="margin-top: 3px;"
                                    >
                                      {{
                                        !!company.DestinationDetails.Active ? $t('Customer.CustomerAddCompanyDialog.Address.Delivery.Different') :
                                        $t('Customer.CustomerAddCompanyDialog.Address.Delivery.Same')
                                      }}
                                    </div>
                                  </template>
                                </v-switch>
                              </v-subheader>
                            </v-col>

                            <template v-if="company.DestinationDetails.Active">
                              <v-col
                                cols="12"
                                sm="6"
                              >
                                <v-text-field
                                  v-model="company.DestinationDetails.Street"
                                  :hint="$t('Customer.CustomerAddCompanyDialog.Field.Street.Hint')"
                                  :label="$t('Customer.CustomerAddCompanyDialog.Field.Street.Label')"
                                  :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Street.Icon')"
                                  :rules="validationRules.companyForm.Street"
                                  required
                                />
                              </v-col>

                              <v-col
                                cols="12"
                                sm="3"
                              >
                                <v-text-field
                                  v-model="company.DestinationDetails.StreetNumber"
                                  :hint="$t('Customer.CustomerAddCompanyDialog.Field.StreetNumber.Hint')"
                                  :label="$t('Customer.CustomerAddCompanyDialog.Field.StreetNumber.Label')"
                                  :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.StreetNumber.Icon')"
                                  :rules="validationRules.companyForm.StreetNumber"
                                  required
                                />
                              </v-col>

                              <v-col
                                cols="12"
                                sm="3"
                              >
                                <v-text-field
                                  v-model="company.DestinationDetails.Postcode"
                                  :hint="$t('Customer.CustomerAddCompanyDialog.Field.Postcode.Hint')"
                                  :label="$t('Customer.CustomerAddCompanyDialog.Field.Postcode.Label')"
                                  :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Postcode.Icon')"
                                  :rules="validationRules.companyForm.Postcode"
                                  required
                                />
                              </v-col>

                              <v-col
                                cols="12"
                                sm="6"
                              >
                                <v-text-field
                                  v-model="company.DestinationDetails.City"
                                  :hint="$t('Customer.CustomerAddCompanyDialog.Field.City.Hint')"
                                  :label="$t('Customer.CustomerAddCompanyDialog.Field.City.Label')"
                                  :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.City.Icon')"
                                  :rules="validationRules.companyForm.City"
                                  required
                                />
                              </v-col>

                              <v-col
                                cols="12"
                                sm="6"
                              >
                                <v-autocomplete
                                  v-model="company.DestinationDetails.CountryId"
                                  :items="countryList"
                                  :label="$t('Customer.CustomerAddCompanyDialog.Field.Country.Label')"
                                  :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Country.Icon')"
                                  :rules="validationRules.companyForm.CountryId"
                                  cache-items
                                  class="pr-2"
                                  item-text="name"
                                  item-value="id"
                                  required
                                >
                                  <template #prepend>
                                    <v-avatar
                                      size="22"
                                      tile
                                    >
                                      <template v-if="company.DestinationDetails.CountryId">
                                        <v-img :src="`/assets/flags/1x1/${String(countryList.find(c => c.id === company.DestinationDetails.CountryId)?.code).toLowerCase()}.svg`" />
                                      </template>
                                      <template v-else>
                                        <v-icon>map</v-icon>
                                      </template>
                                    </v-avatar>
                                  </template>

                                  <template #item="{item}">
                                    <v-list-item-avatar size="34">
                                      <v-img :src="`/assets/flags/1x1/${String(item.code).toLowerCase()}.svg`" />
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                      <v-list-item-title v-safe-html="item.name" />
                                      <v-list-item-subtitle
                                        v-safe-html="item.desc"
                                        class="text-caption"
                                      />
                                    </v-list-item-content>
                                  </template>
                                </v-autocomplete>
                              </v-col>

                              <v-col cols="12">
                                <v-text-field
                                  v-model="company.DestinationDetails.Phone"
                                  :hint="$t('Customer.CustomerAddCompanyDialog.Field.Phone.Hint')"
                                  :label="$t('Customer.CustomerAddCompanyDialog.Field.Phone.Label')"
                                  :prepend-icon="$t('Customer.CustomerAddCompanyDialog.Field.Phone.Icon')"
                                  :rules="validationRules.companyForm.Phone"
                                  required
                                />
                              </v-col>
                            </template>
                          </v-row>
                        </v-container>
                      </v-stepper-content>
                    </v-stepper-items>
                  </v-stepper>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            text
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
          </v-btn>


          <v-btn
            v-if="step === 1"
            :disabled="loading"
            :loading="loading"
            class="blue white--text elevation-0"
            @click="onNextButtonClick"
          >
            {{ $t('Common.Button.Next').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
          <v-btn
            v-if="step === 2"
            :disabled="loading"
            :loading="loading"
            class="blue white--text elevation-0"
            @click="onBackButtonClick"
          >
            {{ $t('Common.Button.Back').toLocaleUpperCase($i18n.locale) }}
          </v-btn>

          <v-btn
            v-if="step === 2"
            :disabled="loading"
            :loading="loading"
            class="green white--text elevation-0"
            @click="onSaveButtonClick"
          >
            {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>

import AppData from '@/mixins/appdata'
import {API, APICall} from '@/api/Api'
import {clone, mergeDeep, validateVAT} from '@/lib/helper/helper'

const clearCompanyData = {
  Id                : null,
  BrandName         : '',
  DistinctiveTitle  : '',
  Profession        : '',
  Phone             : '',
  Email             : '',
  VatNumber         : '',
  Irs               : '',
  Notes             : '',
  County            : '',
  City              : '',
  Street            : '',
  StreetNumber      : '',
  Postcode          : '',
  Country           : '',
  CountryId         : 86,
  CountryCode       : 'GR',
  DestinationDetails: {
    Active      : false,
    County      : '',
    City        : '',
    Street      : '',
    StreetNumber: '',
    Postcode    : '',
    Country     : '',
    Phone       : '',
    CountryId   : 86,
    CountryCode : 'GR'
  }
}

export default {
  name      : 'CustomerCompanySelectDialog',
  components: {},
  mixins    : [AppData],
  props     : {
    data: {
      type   : Object,
      default: undefined
    },
    visible: {
      type   : Boolean,
      default: false
    }
  },
  data() {
    return {
      step            : 1,
      selectedCompany : null,
      loading         : false,
      companyFormValid: false,
      fetchingVatData : false,
      vatNumberError  : '',
      company         : this.defaultEmptyCompany(),
      validationRules : {
        companyForm: {
          BrandName: [
            (v) => !!v || this.$t('Customer.CustomerAddCompanyDialog.Field.BrandName.Errors.Mandatory'),
            (v) => v && v.length >= 2 && v.length <= 512 || this.$t('Customer.CustomerAddCompanyDialog.Field.BrandName.Errors.Valid')
          ],
          DistinctiveTitle: [
            (v) => !v || (v && v.length >= 2 && v.length <= 512) || this.$t('Customer.CustomerAddCompanyDialog.Field.DistinctiveTitle.Errors.Valid')
          ],
          Profession: [
            (v) => !v || (v.length >= 2 && v.length <= 512) || this.$t('Customer.CustomerAddCompanyDialog.Field.Profession.Errors.Valid')
          ],
          Phone: [
            (v) => !v || (v && v.length >= 8 && v.length <= 16) || this.$t('Customer.CustomerAddCompanyDialog.Field.Phone.Errors.Valid')
          ],
          Email: [
            (v) => !v || (v.length >= 0 && v.length <= 96) || this.$t('Customer.CustomerAddEditDialog.Field.Info.Email.Error.Between'),
            (v) => !v || (/^\w+([.\-\\+]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(v)) || this.$t('Customer.CustomerAddEditDialog.Field.Info.Email.Error.Valid')
          ],
          VatNumber: [
            (v) => !!v || this.$t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Errors.Mandatory'),
            (v) => v && validateVAT(this.vatCountryCode, v) || this.$t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Errors.AdvancedValid', {country: this.vatCountryCode}),
            (v) => v && v.length >= 9 && v.length <= 14 || this.$t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Errors.Valid')
          ],
          Irs: [
            (v) => !!v || this.$t('Customer.CustomerAddCompanyDialog.Field.Irs.Errors.Mandatory'),
            (v) => v && v.length >= 2 || this.$t('Customer.CustomerAddCompanyDialog.Field.Irs.Errors.Valid')
          ],
          Notes: [
            (v) => !v || (v && v.length >= 2 && v.length <= 1024) || this.$t('Customer.CustomerAddCompanyDialog.Field.Notes.Errors.Valid')
          ],
          County: [
            (v) => !v || (v && v.length >= 2 && v.length <= 128) || this.$t('Customer.CustomerAddCompanyDialog.Field.County.Errors.Valid')
          ],
          City: [
            (v) => !!v || this.$t('Customer.CustomerAddCompanyDialog.Field.City.Errors.Mandatory'),
            (v) => v && v.length >= 2 && v.length <= 128 || this.$t('Customer.CustomerAddCompanyDialog.Field.City.Errors.Valid')
          ],
          Street: [
            (v) => !!v || this.$t('Customer.CustomerAddCompanyDialog.Field.Street.Errors.Mandatory'),
            (v) => v && v.length >= 2 && v.length <= 128 || this.$t('Customer.CustomerAddCompanyDialog.Field.Street.Errors.Valid')
          ],
          StreetNumber: [
            (v) => !!v || this.$t('Customer.CustomerAddCompanyDialog.Field.StreetNumber.Errors.Mandatory'),
            (v) => v && v.length >= 1 && v.length <= 10 || this.$t('Customer.CustomerAddCompanyDialog.Field.StreetNumber.Errors.Valid')
          ],
          Postcode: [
            (v) => !v || (v && v.length >= 2) || this.$t('Customer.CustomerAddCompanyDialog.Field.Postcode.Errors.Valid')
          ],
          CountryId: [
            (v) => !!v || this.$t('Customer.CustomerAddCompanyDialog.Field.StreetNumber.Errors.Mandatory')
          ]
        }
      }
    }
  },
  computed: {
    isTableOrder() {
      return !!this.data?.order?.TableId
    },
    vatCountryCode() {
      const country = this.countryList.find(country => country.id === this.company.CountryId)
      return country ? (country.code === 'GR' ? 'EL' : country.code === 'GB' ? 'UK' : country.code) : ''
    },

    countryCode() {
      const country = this.countryList.find(country => country.id === this.company.CountryId)
      return country?.code || ''
    },

    countryList() {
      return this.data?.countries || []
    },

    companiesList() {
      return this.data?.companies || []
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    isVisible: {
      handler(newVal) {
        this.$nextTick(() => {
          if (newVal) {
            if (this.data?.data?.Company) {
              this.selectedCompany = this.data?.data?.Company
            } else if (this.data?.order?.CustomerId) {
              const customerCompanies = this.companiesList.filter(company => company.CustomerId === this.data?.order?.CustomerId)
              if (customerCompanies.length === 1) this.selectedCompany = customerCompanies[0]
            }
          }

          this.$bus.$emit('customer-company-select-dialog:visible', newVal)
        })
      },

      immediate: true
    },

    selectedCompany(newVal) {
      if (newVal) {
        this.company = mergeDeep(this.defaultEmptyCompany(), clone(newVal))
      } else {
        this.company = this.defaultEmptyCompany()
      }
    },

    countryCode(newVal) {
      this.company.CountryCode = newVal
    }
  },
  mounted() {
  },
  created() {
    this.$nextTick(() => {
      clearCompanyData.CountryId = this.appCountryId
      clearCompanyData.CountryCode = this.appCountryCode
    })

  },
  beforeDestroy() {
  },
  methods: {
    filterCompanies(item, queryText, itemText) {
      const VatNumber = item.VatNumber?.toLocaleLowerCase(this.$i18n.locale) || ''
      const BrandName = item.BrandName?.toLocaleLowerCase(this.$i18n.locale) || ''
      const DistinctiveTitle = item.DistinctiveTitle?.toLocaleLowerCase(this.$i18n.locale) || ''
      const Phone = item.Phone?.toLocaleLowerCase(this.$i18n.locale) || ''
      const Email = item.Email?.toLocaleLowerCase(this.$i18n.locale) || ''
      const City = item.City?.toLocaleLowerCase(this.$i18n.locale) || ''
      const Street = item.Street?.toLocaleLowerCase(this.$i18n.locale) || ''
      const StreetNumber = item.StreetNumber?.toLocaleLowerCase(this.$i18n.locale) || ''
      const searchText = queryText.toLocaleLowerCase(this.$i18n.locale)

      return VatNumber.includes(searchText)
          || BrandName.includes(searchText)
          || DistinctiveTitle.includes(searchText)
          || Phone.includes(searchText)
          || Email.includes(searchText)
          || City.includes(searchText)
          || Street.includes(searchText)
          || StreetNumber.includes(searchText)
    },

    validateVAT(countryCode, vatNumber, allowUnsupportedCountries = true) {
      const storeVatNumber = this.appConfig?.TAX?.TaxInfo?.Vat || ''
      return validateVAT(countryCode, vatNumber, allowUnsupportedCountries) && vatNumber !== storeVatNumber
    },

    defaultEmptyCompany() {
      return clone(clearCompanyData)
    },

    async fetchDataByVatNumber() {
      if (!this.company.VatNumber || !this.vatCountryCode || !validateVAT(this.vatCountryCode, this.company.VatNumber)) return

      this.vatNumberError = ''
      this.fetchingVatData = true

      const payload = {
        Vat           : this.company.VatNumber,
        VatCountryCode: this.vatCountryCode || 'EL'
      }

      try {
        const response = await API.post(APICall.vatCheck, payload)
        const data = response?.data || null
        const status = data?.status || ''

        if (status === 'success') {
          this.setCompanyDataFromApi(data)
        } else {
          if (!this.selectedCompany) this.vatNumberError = this.$t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Errors.ApiError')
        }
      } catch (e) {
        if (!this.selectedCompany) this.vatNumberError = this.$t('Common.Error.Generic')
      }

      this.fetchingVatData = false
    },

    setCompanyDataFromApi(data) {
      this.company.Irs = data.IrsDescription || ''
      this.company.BrandName = data.BrandName || ''
      this.company.DistinctiveTitle = data.DistinctiveTitle || ''
      this.company.Profession = data.Profession || ''
      this.company.Street = data.Street || ''
      this.company.StreetNumber = data.StreetNumber || ''
      this.company.Postcode = data.Postcode || ''
      this.company.City = data.City || ''

      this.step = 2
    },

    onNextButtonClick() {
      this.fetchDataByVatNumber()
      this.step = 2
    },

    onBackButtonClick() {
      this.step = 1
    },

    onSaveButtonClick() {
      const company = clone(this.company)
      this.$refs.companyForm.validate()

      if (this.companyFormValid) {
        const payload = clone(this.data)

        if (!company.DestinationDetails.Active) company.DestinationDetails = null

        this.$emit('save:company', company, payload)
        this.onCancelButtonClick(false)
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.FieldError'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    onCancelButtonClick(dispatchEvent = true) {
      if (this.$refs.companyForm) this.$refs.companyForm.resetValidation()
      if (dispatchEvent) this.$emit('cancel:company')
      this.company = this.defaultEmptyCompany()
      this.selectedCompany = null
      this.vatNumberError = ''
      this.isVisible = false
    }
  }
}
</script>
